/**
 * Bookingregisteret — artistene, byråene, utkastene og budene.
 *
 * ---------------------------------------------------------------------------------------------
 * HVER SKRIVING INVALIDERER HELE `bookingRegister`-PREFIKSEN, og det er billigere enn det ser ut.
 *
 * De fem svarene henger sammen på måter en liste over nøkler ville tatt feil av: et nytt bud endrer
 * både budhistorikken OG raden i registeret (som viser siste bud) OG artistkortet. Å flytte en boks
 * på brettet endrer brettet OG utkastets sum i velgeren. Å slette en artist endrer registeret OG
 * hvert brett hun sto på.
 *
 * Å navngi undermengden riktig for hver mutasjon er fem regler noen må vedlikeholde, og den ene som
 * blir glemt gir en liste som ligger ett klikk bak — den typen foreldethet ingen melder fra om og
 * alle slutter å stole på. Prefiksen er derimot ÉN regel, og den er alltid riktig.
 *
 * Det som gjør den billig er at prefiksen er smal: `qk.booking` (traktoversikten på nabosiden)
 * ligger UTENFOR den, så et bud her tvinger ingen refetch der.
 */
import {
  bookingAgencyDtoSchema,
  bookingArtistRosterDtoSchema,
  type BookingArtistRosterDto,
  type CreateBookingArtistFormatRequest,
  bookingAgencyListResponseSchema,
  bidDialogueResponseSchema,
  bookingBidDtoSchema,
  bookingBidListResponseSchema,
  bookingBoardResponseSchema,
  bookingDraftDtoSchema,
  bookingDraftItemDtoSchema,
  bookingDraftListResponseSchema,
  agencyArtistProfileResponseSchema,
  bookingRegisterArtistDtoSchema,
  bookingRegisterListResponseSchema,
  promoteDraftResponseSchema,
  artistPlacementsResponseSchema,
  roomDateQueueSchema,
  bookingTermDefaultsSchema,
  bookingPriceHistoryEntrySchema,
  DEFAULT_PAGE_SIZE,
  bookingAgencyHistoryResponseSchema,
  bookingPricingSettingsSchema,
  bookingPriceHistoryResponseSchema,
  type BookingAgencyDto,
  type BookingAgencyListResponse,
  type BidDialogueResponse,
  type BookingBidDto,
  type ListBidDialogueQuery,
  type BookingBidListResponse,
  type BookingBoardResponse,
  type BookingDraftDto,
  type BookingDraftItemDto,
  type BookingDraftListResponse,
  type AgencyArtistProfileResponse,
  type BookingRegisterArtistDto,
  type ResolveArtistIdentityRequest,
  type BookingRegisterListResponse,
  type BookingRegisterSortKey,
  type CreateBookingAgencyRequest,
  type CreateBookingArtistRequest,
  type CreateBookingBidRequest,
  type CreateBookingDraftItemRequest,
  type CreateBookingDraftRequest,
  type UpdateBookingAgencyRequest,
  type UpdateBookingArtistRequest,
  type UpdateBookingBidRequest,
  type UpdateBookingDraftItemRequest,
  type UpdateBookingDraftRequest,
  type PromoteDraftRequest,
  type PromoteDraftResponse,
  type BookingPriceHistoryEntry,
  type BookingAgencyHistoryResponse,
  type BookingPricingSettings,
  type UpdateBookingPricingSettingsRequest,
  type BookingPriceHistoryResponse,
  type CreatePriceHistoryRequest,
  type ConfirmArtistPriceRequest,
  type ResolveArtistOverrideRequest,
  type ArtistPlacementsResponse,
  type RoomDateQueue,
  type BookingTermDefaults,
  type UpdateBookingTermDefaultsRequest,
  sharedAdvanceDtoSchema,
  nightPerformanceResponseSchema,
  type SharedAdvanceDto,
  type BidToNightRequest,
  type NightPerformanceResponse,
} from '@tvitvi/shared';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const REGISTER = '/booking/register';
const AGENCIES = '/booking/agencies';
const DRAFTS = '/booking/drafts';
const ITEMS = '/booking/items';
const BIDS = '/booking/bids';

/**
 * DE FEM FLERVALGSFILTRENE ER KOMMASTRENGER, IKKE ARRAYER (QA-runde 12, #24d).
 *
 * Strengen er det som går på tråden (`?genre=pop,rock`), og den er også det som ligger i URL-en.
 * Å bære en array her ville betydd to former for det samme valget, og — verre — en ny
 * `queryKey`-identitet per render: `['pop','rock']` er aldri `===` seg selv, så React Query ville
 * sett en ny nøkkel hver gang komponenten tegnet seg og hentet lista på nytt i det uendelige.
 * En streng er en verdi.
 */
export interface RegisterFilters {
  search?: string | undefined;
  status?: string | undefined;
  agencyId?: string | undefined;
  shortlisted?: boolean | undefined;
  /* Filtrene fra 0067. Alle fire går til SERVEREN, fordi lista er paget — en avgrensning i
     klienten ville avgrenset innenfor den siden man har hentet. */
  gender?: string | undefined;
  /**
   * SJANGER OG TYPE, FLYTTET TIL SERVEREN (#24d, og det er en rettelse).
   *
   * De to sto som en `filter()` over de radene som tilfeldigvis var hentet — den samme løgnen
   * kommentaren over `minCostMinor` beskriver. Målt 19. september: «Sjanger: pop» på side 1 av 30
   * ga 4 treff av 130, mens telleren fortsatt sa 741.
   */
  genre?: string | undefined;
  artistType?: string | undefined;
  /** «Uten pris» (#24f). Ikke det samme som `maxCostMinor=1`: null kroner er «vi vet ikke». */
  noPrice?: boolean | undefined;
  priceConfirmed?: boolean | undefined;
  minCostMinor?: number | undefined;
  maxCostMinor?: number | undefined;
  /** Køen av uavklarte Spotify-identiteter (sak 89). Serverfilter: køen er ikke en delmengde
      av det som tilfeldigvis er hentet. */
  needsIdentity?: boolean | undefined;
  /** Arkiverte artister i byråets stall (215.29). Skjult som standard. */
  includeArchived?: boolean | undefined;
  /** Bare de skjulte (237.1, «Skjulte»). Uten den står de skjulte ikke i lista. */
  hidden?: boolean | undefined;
  /** «Bare de jeg sammenligner» (QA13 24): id-ene fra nettleserens utvalg, som kommaliste. */
  ids?: string | undefined;
  sort?: BookingRegisterSortKey | undefined;
  direction?: 'asc' | 'desc' | undefined;
}

// ------------------------------------------------------------------------------------ lesing

export function registerQueryOptions(orgId: string, filters: RegisterFilters = {}) {
  return queryOptions({
    queryKey: qk.bookingRegisterList(orgId, filters),
    queryFn: ({ signal }): Promise<BookingRegisterListResponse> =>
      apiRequest({
        path: REGISTER,
        query: {
          ...(filters.search === undefined ? {} : { search: filters.search }),
          ...(filters.status === undefined ? {} : { status: filters.status }),
          ...(filters.agencyId === undefined ? {} : { agencyId: filters.agencyId }),
          ...(filters.shortlisted === undefined ? {} : { shortlisted: filters.shortlisted }),
          ...(filters.gender === undefined ? {} : { gender: filters.gender }),
          ...(filters.genre === undefined ? {} : { genre: filters.genre }),
          ...(filters.artistType === undefined ? {} : { artistType: filters.artistType }),
          ...(filters.noPrice === true ? { noPrice: true } : {}),
          ...(filters.needsIdentity === true ? { needsIdentity: true } : {}),
          ...(filters.hidden === true ? { hidden: true } : {}),
          ...(filters.ids === undefined ? {} : { ids: filters.ids }),
          ...(filters.includeArchived === true ? { includeArchived: true } : {}),
          ...(filters.priceConfirmed === undefined
            ? {}
            : { priceConfirmed: filters.priceConfirmed }),
          ...(filters.minCostMinor === undefined ? {} : { minCostMinor: filters.minCostMinor }),
          ...(filters.maxCostMinor === undefined ? {} : { maxCostMinor: filters.maxCostMinor }),
          ...(filters.sort === undefined ? {} : { sort: filters.sort }),
          ...(filters.direction === undefined ? {} : { direction: filters.direction }),
        },
        schema: bookingRegisterListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * Registeret som en PAGET liste, ikke som de femti første.
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 7. september: «Jeg kan heller ikke bytte sider for å blake videre til andre artister.»
 *
 * Han hadde rett, og det var ikke et manglende knappesett — det var at ingenting spurte etter side
 * to. `registerQueryOptions` henter én side og stopper. Med ti demoartister merket ingen det; med
 * 735 ekte artister ser man alfabetet fram til «B».
 *
 * Markøren finnes allerede hele veien gjennom API-et; det som manglet var at klienten brukte den.
 */
export function registerInfiniteQueryOptions(
  orgId: string,
  filters: RegisterFilters = {},
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  return infiniteQueryOptions({
    /*
     * EGEN NØKKEL, med 'paged' på slutten.
     *
     * Den pagede og den enkle varianten kan ikke dele nøkkel: React Query lagrer en infinite query
     * som { pages, pageParams } og en vanlig som svaret selv, og med samme nøkkel leser den ene den
     * andres form. Favorittlista og brettets artistvelger bruker fortsatt den enkle.
     */
    queryKey: [...qk.bookingRegisterList(orgId, filters), 'paged', pageSize],
    queryFn: ({ pageParam, signal }): Promise<BookingRegisterListResponse> =>
      apiRequest({
        path: REGISTER,
        query: {
          limit: pageSize,
          ...(filters.search === undefined ? {} : { search: filters.search }),
          ...(filters.status === undefined ? {} : { status: filters.status }),
          ...(filters.agencyId === undefined ? {} : { agencyId: filters.agencyId }),
          ...(filters.shortlisted === undefined ? {} : { shortlisted: filters.shortlisted }),
          ...(filters.gender === undefined ? {} : { gender: filters.gender }),
          ...(filters.genre === undefined ? {} : { genre: filters.genre }),
          ...(filters.artistType === undefined ? {} : { artistType: filters.artistType }),
          ...(filters.noPrice === true ? { noPrice: true } : {}),
          ...(filters.needsIdentity === true ? { needsIdentity: true } : {}),
          ...(filters.hidden === true ? { hidden: true } : {}),
          ...(filters.ids === undefined ? {} : { ids: filters.ids }),
          ...(filters.priceConfirmed === undefined
            ? {}
            : { priceConfirmed: filters.priceConfirmed }),
          ...(filters.minCostMinor === undefined ? {} : { minCostMinor: filters.minCostMinor }),
          ...(filters.maxCostMinor === undefined ? {} : { maxCostMinor: filters.maxCostMinor }),
          ...(filters.sort === undefined ? {} : { sort: filters.sort }),
          ...(filters.direction === undefined ? {} : { direction: filters.direction }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        schema: bookingRegisterListResponseSchema,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: BookingRegisterListResponse) =>
      last.hasMore ? last.nextCursor : null,
    ...CACHE.list,
  });
}

/** Husets satser og scorevekter. Én rad per organisasjon, laget av APIET på første oppslag. */
export function pricingSettingsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.bookingPricingSettings(orgId),
    queryFn: ({ signal }): Promise<BookingPricingSettings> =>
      apiRequest({
        path: '/booking/pricing-settings',
        schema: bookingPricingSettingsSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

export function useUpdatePricingSettings(
  orgId: string,
): UseMutationResult<BookingPricingSettings, unknown, UpdateBookingPricingSettingsRequest> {
  return useMutation({
    mutationFn: (body: UpdateBookingPricingSettingsRequest) =>
      apiRequest({
        path: '/booking/pricing-settings',
        method: 'PATCH',
        body,
        schema: bookingPricingSettingsSchema,
      }),
    /*
     * INVALIDERER HELE REGISTERET, ikke bare innstillingen.
     *
     * Satsene bestemmer reise, opphold og mat på hver artist, og vektene bestemmer hver score. En
     * endring her flytter tall over hele lista — og de skrives først på nytt ved neste kjøring av
     * estimatet, som er verdt å vite, men lista skal uansett hente på nytt.
     */
    meta: { invalidates: [qk.bookingRegister(orgId)] },
  });
}

export function registerArtistQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.bookingRegisterArtist(orgId, artistId),
    queryFn: ({ signal }): Promise<BookingRegisterArtistDto> =>
      apiRequest({
        path: `${REGISTER}/${artistId}`,
        schema: bookingRegisterArtistDtoSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

/**
 * BYRÅETS EGEN PROFIL OM DEN SAMME ARTISTEN (sak 116.2, migrering 0151).
 *
 * EGEN SPØRRING OG IKKE ET FELT PÅ ARTISTEN: den krysser husgrensen gjennom en port i basen, og
 * artistlista er 741 rader. Kortet åpnes én om gangen, og det er der forskjellen er verdt å se.
 *
 * Et tomt svar er normalt — byrået trenger ikke være i Tvitvi — så dette er ikke en feiltilstand.
 */
export function agencyArtistProfileQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.agencyArtistProfile(orgId, artistId),
    queryFn: ({ signal }): Promise<AgencyArtistProfileResponse> =>
      apiRequest({
        path: `${REGISTER}/${artistId}/agency-profile`,
        schema: agencyArtistProfileResponseSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}
/**
 * Byrålista — uten de skjulte, eller med dem (#25).
 *
 * `includeHidden` er en del av NØKKELEN og ikke bare av spørringen: de to svarene er to lister, og
 * med samme nøkkel ville «Vis skjulte» skrevet den lange lista inn i den korte og latt hver annen
 * skjerm i appen — artistkortets byrånedtrekk, forespørselen — vise byråer brukeren har skjult.
 */
export function agenciesQueryOptions(orgId: string, includeHidden = false) {
  return queryOptions({
    queryKey: [...qk.bookingAgencies(orgId), { includeHidden }],
    queryFn: ({ signal }): Promise<BookingAgencyListResponse> =>
      apiRequest({
        path: AGENCIES,
        ...(includeHidden ? { query: { includeHidden: true } } : {}),
        schema: bookingAgencyListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function draftsQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.bookingDrafts(orgId, editionId),
    queryFn: ({ signal }): Promise<BookingDraftListResponse> =>
      apiRequest({
        path: `/editions/${editionId}/booking/drafts`,
        schema: bookingDraftListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function boardQueryOptions(orgId: string, draftId: string) {
  return queryOptions({
    queryKey: qk.bookingBoard(orgId, draftId),
    queryFn: ({ signal }): Promise<BookingBoardResponse> =>
      apiRequest({
        path: `${DRAFTS}/${draftId}/board`,
        schema: bookingBoardResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function bidsQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.bookingBids(orgId, artistId),
    queryFn: ({ signal }): Promise<BookingBidListResponse> =>
      apiRequest({
        path: `${REGISTER}/${artistId}/bids`,
        schema: bookingBidListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * ET BUD FRA EN DYPLENKE (QA5 E #2): `?bid=` er vårt eget bud, `?enquiry=` er byråets forespørsel
 * — det er den id-en varsler og delte tråder fra byråets side bærer — oversatt til budet den kom fra.
 */
export function bidLookupQueryOptions(orgId: string, by: 'bid' | 'enquiry', id: string) {
  return queryOptions({
    queryKey: qk.bookingBidLookup(orgId, by, id),
    queryFn: ({ signal }): Promise<BookingBidDto> =>
      apiRequest({
        path: by === 'bid' ? `${BIDS}/${id}` : `${BIDS}/by-enquiry/${id}`,
        schema: bookingBidDtoSchema,
        signal,
      }),
    ...CACHE.list,
    retry: false,
  });
}

// ---------------------------------------------------------------------------------- skriving

/** Prefiksen hver mutasjon under invaliderer. Se filhodet for hvorfor det er én regel og ikke fem. */
/**
 * Prefiksene en skriving i registeret gjør foreldet.
 *
 * `bookingChat` OG `threads` KOM MED 8. september, og de er ikke pynt: en sendt forespørsel
 * skriver teksten sin inn i samtalen, og det er den skrivingen som ÅPNER chatten. Uten disse to
 * blir «Samtale»-fanen stående borte til noen laster siden på nytt — nettopp den slags foreldethet
 * ingen melder fra om, fordi det ser ut som om knappen ikke gjorde noe.
 *
 * `threads` fordi den samme meldingen legger en rad i innboksen.
 */
const invalidates = (orgId: string) => ({
  /* `dealSheets`: et nytt bud på en sak med ark er en ny versjon av arket (C3). */
  invalidates: [
    qk.bookingRegister(orgId),
    qk.bookingChat(orgId),
    qk.threads(orgId),
    qk.dealSheets(orgId),
    /* QA16 funn 13: the one bid window also bids from the concert page; the artist's status there moves. */
    qk.orgConcerts(orgId),
    /* QA16 307.2: et trukket bud avlyser arrangementet det står på. */
    qk.venueHouse(orgId),
  ],
});

export function useCreateArtist(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, CreateBookingArtistRequest> {
  return useMutation({
    mutationFn: (body: CreateBookingArtistRequest) =>
      apiRequest({
        path: REGISTER,
        method: 'POST',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export interface UpdateArtistInput {
  artistId: string;
  body: UpdateBookingArtistRequest;
}

export interface ResolveIdentityInput {
  artistId: string;
  body: ResolveArtistIdentityRequest;
}

/**
 * Mennesket avgjorde identiteten (sak 89).
 *
 * Egen mutasjon framfor en variant av oppdateringen: det er en annen handling med sin egen
 * skranke — et ja krever en id — og API-et har sin egen rute for nettopp det.
 */
export function useResolveIdentity(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, ResolveIdentityInput> {
  return useMutation({
    mutationFn: ({ artistId, body }: ResolveIdentityInput) =>
      apiRequest({
        path: REGISTER + '/' + artistId + '/identity',
        method: 'PATCH',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useUpdateArtist(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, UpdateArtistInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ artistId, body }: UpdateArtistInput) =>
      apiRequest({
        path: `${REGISTER}/${artistId}`,
        method: 'PATCH',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    /*
     * ---------------------------------------------------------------------------------------
     * SVARET SKRIVES RETT INN I DETALJEN (QA runde 7, punkt 8).
     *
     * Rapporten: «Klikket sender PATCH 200 og tabellen flipper, men popupen står igjen med Sett
     * på ønskelista.» Popupen tegner `artist.shortlisted` fra detaljspørringen, og den skulle
     * vært ugyldiggjort av sveipet — nøkkelen ligger under `bookingRegister`, som treffer på
     * prefiks. Jeg fant ikke hvorfor refetchen ikke landet.
     *
     * Derfor er dette ikke en gjetning på årsaken, men en fjerning av avhengigheten: PATCH-en
     * svarer med HELE den oppdaterte raden, og den raden ER detaljen. Å skrive den inn gjør
     * stjerna riktig i samme øyeblikk, uten en rundtur — og sveipet under står igjen for lista,
     * som bærer den samme artisten i en annen form.
     *
     * Samme grep som `useSetAdvanceHouseDocuments`: der svaret er det nye innholdet, er en
     * invalidering en omvei.
     */
    onSuccess: (artist) => {
      queryClient.setQueryData(qk.bookingRegisterArtist(orgId, artist.id), artist);
    },
    meta: invalidates(orgId),
  });
}

/** Arkiver fra stallen, eller hent fram igjen (215.29). Cella er `artist:archive`. */
export function useArchiveArtist(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, { artistId: string; archived: boolean }> {
  return useMutation({
    mutationFn: ({ artistId, archived }: { artistId: string; archived: boolean }) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/archive`,
        method: 'PATCH',
        body: { archived },
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: { invalidates: [qk.bookingRegister(orgId), qk.marketplaceAll(orgId)] },
  });
}

/** «Skjul» og «Vis igjen» på en artist fra skanningen eller importen (237.1). */
export function useHideArtist(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, { artistId: string; hidden: boolean }> {
  return useMutation({
    mutationFn: ({ artistId, hidden }: { artistId: string; hidden: boolean }) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/hidden`,
        method: 'PATCH',
        body: { hidden },
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useDeleteArtist(orgId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (artistId: string) =>
      apiRequest({ path: `${REGISTER}/${artistId}`, method: 'DELETE' }).then(() => undefined),
    /*
     * QA13k F16: DEN SLETTEDE ARTISTENS SPØRRINGER INVALIDERES IKKE. Skuffen står ennå åpen når
     * invalideringen kjører, og `meta.invalidates` hentet profilen, budene, historikken og samtalen
     * til en artist som ikke finnes — fire 404 i konsollen. Resten av registeret hentes som før.
     */
    onSuccess: (_done, artistId) => {
      for (const key of invalidates(orgId).invalidates) {
        void queryClient.invalidateQueries({
          queryKey: key,
          predicate: (query) => !JSON.stringify(query.queryKey).includes(artistId),
        });
      }
    },
  });
}

export function useCreateAgency(
  orgId: string,
): UseMutationResult<BookingAgencyDto, unknown, CreateBookingAgencyRequest> {
  return useMutation({
    mutationFn: (body: CreateBookingAgencyRequest) =>
      apiRequest({ path: AGENCIES, method: 'POST', body, schema: bookingAgencyDtoSchema }),
    meta: invalidates(orgId),
  });
}

export interface UpdateAgencyInput {
  agencyId: string;
  body: UpdateBookingAgencyRequest;
}

export function useUpdateAgency(
  orgId: string,
): UseMutationResult<BookingAgencyDto, unknown, UpdateAgencyInput> {
  return useMutation({
    mutationFn: ({ agencyId, body }: UpdateAgencyInput) =>
      apiRequest({
        path: `${AGENCIES}/${agencyId}`,
        method: 'PATCH',
        body,
        schema: bookingAgencyDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useDeleteAgency(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (agencyId: string) =>
      apiRequest({ path: `${AGENCIES}/${agencyId}`, method: 'DELETE' }).then(() => undefined),
    meta: invalidates(orgId),
  });
}

/** Angre på «slett byrå» (QA5 E #22). */
export function useRestoreAgency(
  orgId: string,
): UseMutationResult<BookingAgencyDto, unknown, string> {
  return useMutation({
    mutationFn: (agencyId: string) =>
      apiRequest({
        path: `${AGENCIES}/${agencyId}/restore`,
        method: 'POST',
        schema: bookingAgencyDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export interface SetAgencyHiddenInput {
  agencyId: string;
  hidden: boolean;
}

/** «Skjul byrået for meg» / «vis det igjen» (#25). Per menneske — se kontrakten. */
export function useSetAgencyHidden(
  orgId: string,
): UseMutationResult<BookingAgencyDto, unknown, SetAgencyHiddenInput> {
  return useMutation({
    mutationFn: ({ agencyId, hidden }: SetAgencyHiddenInput) =>
      apiRequest({
        path: `${AGENCIES}/${agencyId}/hidden`,
        method: 'PATCH',
        body: { hidden },
        schema: bookingAgencyDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

/** «Bekreft pris» (#27c). Svarer med hele artisten, så kortet og lista blir enige med én gang. */
export function useConfirmArtistPrice(
  orgId: string,
  artistId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, ConfirmArtistPriceRequest> {
  return useMutation({
    mutationFn: (body: ConfirmArtistPriceRequest) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/prices/confirm`,
        method: 'POST',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export interface ConfirmPriceInput {
  artistId: string;
  body: ConfirmArtistPriceRequest;
}

/**
 * «Bekreft pris» for hvilken som helst rad — hurtigredigeringen i registeret (0440). Den samme
 * ruten som prisfanen; artisten står i `mutate()` fordi én tabell bekrefter mange.
 */
export function useConfirmPriceFor(
  orgId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, ConfirmPriceInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ artistId, body }: ConfirmPriceInput) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/prices/confirm`,
        method: 'POST',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    onSuccess: (artist) => {
      queryClient.setQueryData(qk.bookingRegisterArtist(orgId, artist.id), artist);
    },
    meta: invalidates(orgId),
  });
}

export interface UpdateFormatFeeInput {
  artistId: string;
  formatId: string;
  askingFeeMinor: number | null;
}

/**
 * HONORAR-CELLA I REGISTERET (bølge 3 H): standardformatets pris, gjennom den samme formatruten som
 * formatkortet bruker (`PATCH /booking/roster/formats/:id`, `performance:update`). Svaret er hele
 * rosteren og skrives rett inn; registeret (og dermed Honorar-kolonnen) ugyldiggjøres av sveipet —
 * det er det `useUpdateFormat` i byråets roster-spørringer ikke gjør (OVERSIKT §4 #2).
 */
export function useUpdateFormatFeeFor(
  orgId: string,
): UseMutationResult<BookingArtistRosterDto, unknown, UpdateFormatFeeInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ formatId, askingFeeMinor }: UpdateFormatFeeInput) =>
      apiRequest({
        path: `/booking/roster/formats/${formatId}`,
        method: 'PATCH',
        body: { askingFeeMinor },
        schema: bookingArtistRosterDtoSchema,
      }),
    onSuccess: (roster, { artistId }) => {
      queryClient.setQueryData(qk.artistRoster(orgId, artistId), roster);
    },
    meta: invalidates(orgId),
  });
}

export interface AddFirstFormatInput {
  artistId: string;
  body: CreateBookingArtistFormatRequest;
}

/**
 * HONORAR PÅ EN ARTIST UTEN FORMATER (bølge 3 H): det første formatet, gjennom den eksisterende
 * ruten (`POST /booking/register/:id/roster/formats`, `performance:update`). API-et gjør det første
 * formatet til standard av seg selv, så prisen er standardformatets pris med én gang.
 */
export function useAddFirstFormatFor(
  orgId: string,
): UseMutationResult<BookingArtistRosterDto, unknown, AddFirstFormatInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ artistId, body }: AddFirstFormatInput) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/roster/formats`,
        method: 'POST',
        body,
        schema: bookingArtistRosterDtoSchema,
      }),
    onSuccess: (roster, { artistId }) => {
      queryClient.setQueryData(qk.artistRoster(orgId, artistId), roster);
    },
    meta: invalidates(orgId),
  });
}

/** «Bruk ny» / «behold min» på et felt kilden har flyttet på (K-6). */
export function useResolveArtistOverride(
  orgId: string,
  artistId: string,
): UseMutationResult<BookingRegisterArtistDto, unknown, ResolveArtistOverrideRequest> {
  return useMutation({
    mutationFn: (body: ResolveArtistOverrideRequest) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/overrides`,
        method: 'PATCH',
        body,
        schema: bookingRegisterArtistDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useCreateDraft(
  orgId: string,
): UseMutationResult<BookingDraftDto, unknown, CreateBookingDraftRequest> {
  return useMutation({
    mutationFn: (body: CreateBookingDraftRequest) =>
      apiRequest({ path: DRAFTS, method: 'POST', body, schema: bookingDraftDtoSchema }),
    meta: invalidates(orgId),
  });
}

export interface UpdateDraftInput {
  draftId: string;
  body: UpdateBookingDraftRequest;
}

export function useUpdateDraft(
  orgId: string,
): UseMutationResult<BookingDraftDto, unknown, UpdateDraftInput> {
  return useMutation({
    mutationFn: ({ draftId, body }: UpdateDraftInput) =>
      apiRequest({
        path: `${DRAFTS}/${draftId}`,
        method: 'PATCH',
        body,
        schema: bookingDraftDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useDeleteDraft(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (draftId: string) =>
      apiRequest({ path: `${DRAFTS}/${draftId}`, method: 'DELETE' }).then(() => undefined),
    /*
     * BARE LISTENE (QA5 E #12). Hele registerprefiksen hentet også brettet til utkastet som nettopp
     * var slettet — det sto fortsatt på skjermen — og konsollen fikk en 404 «No such draft.».
     * Når lista er ny, bytter brettet selv til et utkast som finnes.
     */
    meta: { invalidates: [qk.bookingDraftsAll(orgId)] },
  });
}

export interface AddItemInput {
  draftId: string;
  body: CreateBookingDraftItemRequest;
}

export function useAddDraftItem(
  orgId: string,
): UseMutationResult<BookingDraftItemDto, unknown, AddItemInput> {
  return useMutation({
    mutationFn: ({ draftId, body }: AddItemInput) =>
      apiRequest({
        path: `${DRAFTS}/${draftId}/items`,
        method: 'POST',
        body,
        schema: bookingDraftItemDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export interface UpdateItemInput {
  itemId: string;
  body: UpdateBookingDraftItemRequest;
}

export function useUpdateDraftItem(
  orgId: string,
): UseMutationResult<BookingDraftItemDto, unknown, UpdateItemInput> {
  return useMutation({
    mutationFn: ({ itemId, body }: UpdateItemInput) =>
      apiRequest({
        path: `${ITEMS}/${itemId}`,
        method: 'PATCH',
        body,
        schema: bookingDraftItemDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useRemoveDraftItem(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest({ path: `${ITEMS}/${itemId}`, method: 'DELETE' }).then(() => undefined),
    meta: invalidates(orgId),
  });
}

/** Angre på «Ta av brettet» (215.28): den samme raden tilbake, så budet henger på igjen. */
export function useRestoreDraftItem(
  orgId: string,
): UseMutationResult<BookingDraftItemDto, unknown, string> {
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest({
        path: `${ITEMS}/${itemId}/restore`,
        method: 'POST',
        schema: bookingDraftItemDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useCreateBid(
  orgId: string,
): UseMutationResult<BookingBidDto, unknown, CreateBookingBidRequest> {
  return useMutation({
    mutationFn: (body: CreateBookingBidRequest) =>
      apiRequest({ path: BIDS, method: 'POST', body, schema: bookingBidDtoSchema }),
    /* QA16 funn 2: et bud på et arrangement legger innslaget der, og et bud i en sal holder den. */
    meta: {
      invalidates: [
        ...invalidates(orgId).invalidates,
        qk.venueHouse(orgId),
        qk.performances(orgId),
        qk.calendar(orgId),
      ],
    },
  });
}

export interface PromoteInput {
  draftId: string;
  body: PromoteDraftRequest;
}

export interface UpdateBidInput {
  bidId: string;
  body: UpdateBookingBidRequest;
}

export function useUpdateBid(
  orgId: string,
): UseMutationResult<BookingBidDto, unknown, UpdateBidInput> {
  return useMutation({
    mutationFn: ({ bidId, body }: UpdateBidInput) =>
      apiRequest({ path: `${BIDS}/${bidId}`, method: 'PATCH', body, schema: bookingBidDtoSchema }),
    meta: invalidates(orgId),
  });
}

/**
 * QA16 307.2: «ANGRE» ETTER «TREKK BUDET». Budet, avtalearket og arrangementet står igjen slik de
 * sto; arrangementet som ble avlyst, er tilbake. Bare rett etter trekket.
 */
export function useReinstateBid(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: async (bidId: string) => {
      await apiRequest({ path: `${BIDS}/${bidId}/reinstate`, method: 'POST' });
    },
    meta: invalidates(orgId),
  });
}

/* «Godta motbudet» og «Gi nytt bud» (F22) er borte: motbud og godta går gjennom avtalearket (C3, `features/deal-sheet`). */

/**
 * FESTIVALENS VEI INN I DEN DELTE ADVANCEN (§4.1).
 *
 * Byråets rute tar en forespørsel; festivalen har en budrad, og de to er den samme forhandlingen
 * sett fra hvert sitt hus. Serveren oversetter — se `app.enquiry_for_bid` (0124).
 *
 * SVARER `null` NÅR BYRÅET IKKE ER I TVITVI, som er normaltilfellet i dag. Da finnes det ingen å
 * dele en advance med, og festivalen fører sin egen på programposten som før.
 */
export function useOpenAdvanceForBid(
  orgId: string,
): UseMutationResult<SharedAdvanceDto | null, unknown, string> {
  return useMutation({
    mutationFn: (bidId: string) =>
      apiRequest({
        path: `${BIDS}/${bidId}/advance`,
        method: 'POST',
        schema: sharedAdvanceDtoSchema.nullable(),
      }),
    meta: invalidates(orgId),
  });
}

/**
 * «LAG ARRANGEMENT» PÅ ET GODTATT BUD (sak 230). Lager programposten og arrangementet i salen, så
 * både Booking, husets arrangementer og programposten lastes på nytt.
 */
export function useBidToNight(
  orgId: string,
): UseMutationResult<
  NightPerformanceResponse,
  unknown,
  { bidId: string; body: BidToNightRequest }
> {
  return useMutation({
    mutationFn: ({ bidId, body }) =>
      apiRequest({
        path: BIDS + '/' + bidId + '/to-night',
        method: 'POST',
        body,
        schema: nightPerformanceResponseSchema,
      }),
    meta: {
      invalidates: [
        ...invalidates(orgId).invalidates,
        qk.venueHouse(orgId),
        qk.performances(orgId),
        qk.calendar(orgId),
        qk.shifts(orgId),
      ],
    },
  });
}

export function useDeleteBid(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (bidId: string) =>
      apiRequest({ path: `${BIDS}/${bidId}`, method: 'DELETE' }).then(() => undefined),
    meta: invalidates(orgId),
  });
}

/**
 * Å skyve utkastet over i årgangens ekte program.
 *
 * INVALIDERER BÅDE BOOKINGREGISTERET OG PROGRAMPOSTENE, som er den ene mutasjonen her som rører
 * noe utenfor sin egen prefiks: den lager ekte programposter, og en Lineup-side som lå åpen i en
 * annen fane ville ellers stått uten dem til noen lastet på nytt.
 */
export function usePromoteDraft(
  orgId: string,
): UseMutationResult<PromoteDraftResponse, unknown, PromoteInput> {
  return useMutation({
    mutationFn: ({ draftId, body }: PromoteInput) =>
      apiRequest({
        path: `${DRAFTS}/${draftId}/promote`,
        method: 'POST',
        body,
        schema: promoteDraftResponseSchema,
      }),
    /*
     * ---------------------------------------------------------------------------------------
     * OVERFØRINGEN RØRER MER ENN LISTA (QA runde 7, P0 #2).
     *
     * «Flytt over i programmet» lager programposten OG — siden 0181 — tømmer plukkerens ventende
     * valg inn i den: mennesker, management og kopier av filene. Sveipet nevnte bare
     * bookingregisteret og programpostlista, så den som gikk rett inn på den nye programposten
     * møtte 0 i reisefølget og tomme advance-seksjoner. Det QA så som «kommer ikke over av seg
     * selv» var delvis dette: radene fantes, skjermen hadde hentet før de ble skrevet.
     *
     * `performanceAdvance` og `attachments` er hver sin spørring, og filene i en seksjon er en
     * tredje. Grenene er små, sveipet er billig, og alternativet er å gjette riktig hver gang.
     */
    meta: {
      invalidates: [
        qk.bookingRegister(orgId),
        qk.performances(orgId),
        qk.performanceAdvance(orgId),
        qk.attachments(orgId),
      ],
    },
  });
}

// ------------------------------------------------------------------------- prishistorikk

/**
 * Byråhistorikken — hvem som har representert artisten, og når vi så det.
 *
 * Egen forespørsel og ikke et felt på artisten: registeret lister sju hundre rader, og en
 * historikk per rad ville vært sju hundre historikker ingen leser. Kortet henter den når det
 * åpnes, som prishistorikken.
 */
export function agencyHistoryQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.bookingAgencyHistory(orgId, artistId),
    queryFn: ({ signal }): Promise<BookingAgencyHistoryResponse> =>
      apiRequest({
        path: `${REGISTER}/${artistId}/agency-history`,
        schema: bookingAgencyHistoryResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function priceHistoryQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.bookingPrices(orgId, artistId),
    queryFn: ({ signal }): Promise<BookingPriceHistoryResponse> =>
      apiRequest({
        path: `${REGISTER}/${artistId}/prices`,
        schema: bookingPriceHistoryResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useAddPriceHistory(
  orgId: string,
  artistId: string,
): UseMutationResult<BookingPriceHistoryEntry, unknown, CreatePriceHistoryRequest> {
  return useMutation({
    mutationFn: (body: CreatePriceHistoryRequest) =>
      apiRequest({
        path: `${REGISTER}/${artistId}/prices`,
        method: 'POST',
        body,
        schema: bookingPriceHistoryEntrySchema,
      }),
    meta: invalidates(orgId),
  });
}

export function useRemovePriceHistory(
  orgId: string,
  _artistId: string,
): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (entryId: string) =>
      apiRequest({ path: `/booking/prices/${entryId}`, method: 'DELETE' }).then(() => undefined),
    meta: invalidates(orgId),
  });
}

// ------------------------------------------------------- plassering og standardvilkår

/**
 * HVOR ARTISTEN STÅR, og hvorfor et bud eventuelt er sperret.
 *
 * Hentes når budskjemaet åpnes, ikke med lista: svaret er en join over fem tabeller, og det er
 * bortkastet for nittini artister man ikke skal by på.
 */
export function placementsQueryOptions(orgId: string, artistId: string) {
  return queryOptions({
    queryKey: qk.bookingPlacements(orgId, artistId),
    queryFn: ({ signal }): Promise<ArtistPlacementsResponse> =>
      apiRequest({
        path: `${REGISTER}/${artistId}/placements`,
        schema: artistPlacementsResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** QA15 C3: salens kø på datoen budet gjelder — hva som står der, og hvilken plass budet får. */
export function roomQueueQueryOptions(
  orgId: string,
  roomId: string,
  date: string,
  nightId?: string,
) {
  return queryOptions({
    queryKey: qk.bookingRoomQueue(orgId, roomId, date, nightId),
    queryFn: ({ signal }): Promise<RoomDateQueue> =>
      apiRequest({
        path: `/booking/rooms/${roomId}/queue`,
        query: nightId === undefined ? { date } : { date, nightId },
        schema: roomDateQueueSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function termDefaultsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.bookingTermDefaults(orgId),
    queryFn: ({ signal }): Promise<BookingTermDefaults> =>
      apiRequest({
        path: '/booking/term-defaults',
        schema: bookingTermDefaultsSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

export function useUpdateTermDefaults(
  orgId: string,
): UseMutationResult<BookingTermDefaults, unknown, UpdateBookingTermDefaultsRequest> {
  return useMutation({
    mutationFn: (body: UpdateBookingTermDefaultsRequest) =>
      apiRequest({
        path: '/booking/term-defaults',
        method: 'PATCH',
        body,
        schema: bookingTermDefaultsSchema,
      }),
    meta: invalidates(orgId),
  });
}

/**
 * DIALOGEN — all kontakt med byråene, på tvers av artister.
 *
 * Filteret ligger i NØKKELEN og ikke bare i forespørselen, som resten av huset: to filtre er to
 * svar, og et hurtiglager som blandet dem ville vist «venter svar» sine tolv rader under «besvart».
 */
export function bidDialogueQueryOptions(orgId: string, query: ListBidDialogueQuery) {
  return queryOptions({
    queryKey: qk.bookingDialogue(orgId, query),
    queryFn: ({ signal }): Promise<BidDialogueResponse> =>
      apiRequest({
        path: '/booking/bids',
        query,
        schema: bidDialogueResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}
